import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetRetailDetailQuery,
    useLazySearchPartForRetailQuery,
    useLazySearchPartForRetailListQuery,
    useCreateDealerRetailMutation,
    useGetRetailPaymentMethodsQuery,
    useApproveOrRejectRetailMutation,
    useApproveOrRejectCancelRetailMutation,
    useGetRetailDealersQuery
} from '../../../../api/endpoints/parts';
import { useGetPreAccountingKdvQuery } from '../../../../api/endpoints/preAccounting';
import { useGetParentWarehousesQuery } from '../../../../api/endpoints/dealerWarehouse';
import { usePreAccountingCustomerOptions } from '../../../../hooks/usePreAccountingCustomerOptions';
import { useLazyGetCustomerDiscountRatesQuery } from '../../../../api';
import { useGetCustomerByIdQuery } from '../../../../api/customerApi';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';

const discountTypes = [
    { label: 'Yok', value: 0 },
    { label: 'Yüzde', value: 1 },
    { label: 'Tutar', value: 2 }
];

const RetailSalesDetailDialog = ({
    visible,
    onHide,
    retailId,
    isCreateMode = false,
    isApprovalMode = false,
    isCancelApprovalMode = false,
    customerType = 0,
    cancelInfo = null,
    onSuccess
}) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const toast = useRef(null);
    const userParams = useSelector(selectUserParams);
    const dealerId = userParams?.dealerId ?? 0;
    const languageId = getCurrentLanguageId();
    const browserLocale = navigator.language || 'tr-TR';
    const [partFilterValue, setPartFilterValue] = useState('');
    const [partSearchText, setPartSearchText] = useState('');
    const [debouncedPartSearch, setDebouncedPartSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedPartSearch(partSearchText), 600);
        return () => clearTimeout(t);
    }, [partSearchText]);

    const [approveOrReject, { isLoading: isApproving }] = useApproveOrRejectRetailMutation();
    const [approveOrRejectCancel, { isLoading: isProcessingCancel }] = useApproveOrRejectCancelRetailMutation();

    // Cancel approval state (only used when isCancelApprovalMode === true)
    const [cancelReject, setCancelReject] = useState({ mode: false, note: '' });
    const { mode: cancelRejectMode, note: cancelRejectNote } = cancelReject;

    const [header, setHeader] = useState({
        invoiceDate: null,
        invoiceSeriNo: '',
        maturityDate: null,
        warehouseId: null,
        paymentMethod: null,
        customer: null,
        invoiceAddress: '',
        currency: 'TRY',
        netTotalAmount: 0,
        kdvliTutar: 0,
        note: ''
    });
    const [parts, setParts] = useState([]);
    const [selectedPartCode, setSelectedPartCode] = useState(null);
    const [partOptions, setPartOptions] = useState([]);
    const [isLoadingParts, setIsLoadingParts] = useState(false);

    const { data: detailData, isFetching: isLoadingDetail } = useGetRetailDetailQuery({ RetailId: retailId }, { skip: !visible || !retailId || isCreateMode });

    // Bayi vs Müşteri: create modunda prop'tan; inceleme modunda backend'in döndüğü customerType önceliklidir.
    const effectiveCustomerType = isCreateMode
        ? customerType
        : (detailData?.customerType ?? customerType);

    const { data: warehouses = [] } = useGetParentWarehousesQuery({ appBranchId: dealerId, languageId }, { skip: !visible || !dealerId });

    const { data: paymentMethodsData = [] } = useGetRetailPaymentMethodsQuery({ LanguageId: languageId }, { skip: !visible });

    // Fetch KDV rate — only in create mode. Inspection mode uses API totals directly.
    const { data: kdvRate } = useGetPreAccountingKdvQuery(undefined, { skip: !visible || !isCreateMode });

    // Customer search state
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
    const [customerFilterValue, setCustomerFilterValue] = useState('');

    const [editCustomerId, setEditCustomerId] = useState(null);
    const [customerFromId, setCustomerFromId] = useState(null);

    // Dealer search state
    const [dealerSearchText, setDealerSearchText] = useState('');
    const [debouncedDealerSearch, setDebouncedDealerSearch] = useState('');
    const [dealerFilterValue, setDealerFilterValue] = useState('');

    const [editDealerId, setEditDealerId] = useState(null);
    const [dealerFromId, setDealerFromId] = useState(null);

    // Debounce customer search - 600ms delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCustomerSearch(customerSearchText);
        }, 600);

        return () => clearTimeout(timer);
    }, [customerSearchText]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDealerSearch(dealerSearchText);
        }, 600);

        return () => clearTimeout(timer);
    }, [dealerSearchText]);

    // Fetch customers for dropdown
    const { customerOptions: fetchedCustomerOptions } = usePreAccountingCustomerOptions({
        SearchText: debouncedCustomerSearch,
        RowCount: 50,
        skip: !visible
    });

    const { data: customerByIdData } = useGetCustomerByIdQuery(
        editCustomerId && effectiveCustomerType === 0 ? editCustomerId : skipToken
    );

    useEffect(() => {
        if (customerByIdData && editCustomerId) {
            const namePart = customerByIdData.name || '';
            const surnamePart = customerByIdData.surname && customerByIdData.surname !== '-' ? customerByIdData.surname : '';
            const fullName = `${namePart} ${surnamePart}`.trim() || `Müşteri #${editCustomerId}`;
            
            const customerOption = {
                label: fullName,
                value: customerByIdData.id
            };
            
            setCustomerFromId(customerOption);
        }
    }, [customerByIdData, editCustomerId]);

    const customerOptionsRef = useRef([]);

    const customerOptions = useMemo(() => {
        let options = [];
        
        if (customerFromId) {
            options.push(customerFromId);
        }
        
        if (fetchedCustomerOptions && fetchedCustomerOptions.length > 0) {
            fetchedCustomerOptions.forEach(opt => {
                if (!options.some(o => o.value === opt.value)) {
                    options.push(opt);
                }
            });
            customerOptionsRef.current = options;
            return options;
        }
        
        if (customerSearchText && customerOptionsRef.current.length > 0) {
            if (customerFromId && !customerOptionsRef.current.some(o => o.value === customerFromId.value)) {
                return [customerFromId, ...customerOptionsRef.current];
            }
            return customerOptionsRef.current;
        }
        
        return options;
    }, [fetchedCustomerOptions, customerSearchText, customerFromId]);

    const { data: dealersData = [] } = useGetRetailDealersQuery(
        { SearchText: debouncedDealerSearch, RowCount: 50 }, 
        { skip: !visible }
    );

    const { data: dealerByIdData = [] } = useGetRetailDealersQuery(
        editDealerId ? { Id: editDealerId, RowCount: 1 } : skipToken
    );

    useEffect(() => {
        if (dealerByIdData && dealerByIdData.length > 0 && editDealerId) {
            const dealer = dealerByIdData[0];
            const dealerOption = {
                label: dealer.name,
                value: Number(dealer.id)
            };
            
            setDealerFromId(dealerOption);
        }
    }, [dealerByIdData, editDealerId]);

    const dealerOptionsRef = useRef([]);

    const dealerOptions = useMemo(() => {
        const addIfMissing = (list, option) =>
            list.some((o) => o.value === option.value) ? list : [...list, option];

        const baseOptions = dealerFromId ? [dealerFromId] : [];
        const fetchedOptions = (dealersData || []).reduce((acc, d) => {
            const dealerId = Number(d.id);
            return addIfMissing(acc, { label: d.name, value: dealerId });
        }, baseOptions);

        if (dealersData?.length) {
            dealerOptionsRef.current = fetchedOptions;
            return fetchedOptions;
        }

        if (dealerSearchText && dealerOptionsRef.current.length) {
            return dealerFromId ? addIfMissing(dealerOptionsRef.current, dealerFromId) : dealerOptionsRef.current;
        }

        return baseOptions;
    }, [dealersData, dealerFromId, dealerSearchText]);

    const [searchPart] = useLazySearchPartForRetailQuery();
    const [searchPartList] = useLazySearchPartForRetailListQuery();
    const [createRetail, { isLoading: isCreating }] = useCreateDealerRetailMutation();
    const [getCustomerDiscountRates] = useLazyGetCustomerDiscountRatesQuery();
    
    // Current customer discount rate
    const [currentPartsDiscountRate, setCurrentPartsDiscountRate] = useState(0);

    const warehouseOptions = useMemo(() => warehouses.map((w) => ({ label: w.warehouseName || w.name, value: w.id })), [warehouses]);

    // Onay/İptal Onay modunda depo dropdown'ı için otomatik olarak ilk depoyu seç.
    useEffect(() => {
        if (!visible) return;
        if (!isApprovalMode && !isCancelApprovalMode) return;
        if (header.warehouseId != null) return;
        if (!warehouseOptions || warehouseOptions.length === 0) return;
        setHeader((h) => ({ ...h, warehouseId: warehouseOptions[0].value }));
    }, [visible, isApprovalMode, isCancelApprovalMode, warehouseOptions, header.warehouseId]);

    const paymentMethodOptions = useMemo(() => paymentMethodsData.map((p) => ({ label: p.name, value: Number(p.id) })), [paymentMethodsData]);

    const kdvRateValue = useMemo(() => (kdvRate != null ? parseFloat(kdvRate) || 0 : 0), [kdvRate]);
    const kdvFactor = 1 + kdvRateValue / 100;

    // Brüt toplam — tablodaki tüm parçaların unitPrice * quantity toplamı (indirim öncesi)
    const grossTotalAmount = useMemo(() => {
        return (parts || []).reduce((sum, p) => {
            const qty = p.quantity || 1;
            const price = p.netUnitPrice ?? 0;
            return sum + qty * price;
        }, 0);
    }, [parts]);

    // Toplam İndirim — brüt ile Toplam Tutar (KDV hariç) arasındaki fark
    const totalDiscount = useMemo(() => {
        const diff = grossTotalAmount - (header.netTotalAmount || 0);
        return diff > 0 ? diff : 0;
    }, [grossTotalAmount, header.netTotalAmount]);

    // In inspection mode we don't fetch the KDV rate; derive an effective rate from
    // the two values returned by the API just for the display badge.
    const displayKdvRate = useMemo(() => {
        if (isCreateMode) return kdvRate;
        const net = header.netTotalAmount || 0;
        const kdvli = header.kdvliTutar || 0;
        if (net <= 0 || kdvli <= 0) return null;
        const rate = (kdvli / net - 1) * 100;
        return Math.round(rate * 100) / 100;
    }, [isCreateMode, kdvRate, header.netTotalAmount, header.kdvliTutar]);

    const showKdvBlock = isCreateMode
        ? kdvRate != null
        : (header.kdvliTutar || 0) > 0;

    // Sync kdvliTutar whenever KDV rate becomes available / changes (create mode only)
    useEffect(() => {
        if (!isCreateMode) return;
        if (kdvRate == null) return;
        setHeader((h) => ({
            ...h,
            kdvliTutar: (h.netTotalAmount || 0) * (1 + (parseFloat(kdvRate) || 0) / 100)
        }));
    }, [kdvRate, isCreateMode]);

    const resetSearchState = () => {
        setCustomerSearchText('');
        setDebouncedCustomerSearch('');
        setCustomerFilterValue('');
        setDealerSearchText('');
        setDebouncedDealerSearch('');
        setDealerFilterValue('');
    };

    const resetEditState = () => {
        setEditCustomerId(null);
        setCustomerFromId(null);
        setEditDealerId(null);
        setDealerFromId(null);
    };

    const resetCreateState = () => {
        setHeader({
            invoiceDate: new Date(),
            invoiceSeriNo: '',
            maturityDate: null,
            warehouseId: null,
            paymentMethod: null,
            customer: null,
            invoiceAddress: '',
            currency: 'TRY',
            netTotalAmount: 0,
            kdvliTutar: 0,
            note: ''
        });
        setParts([]);
        setSelectedPartCode(null);
        setPartOptions([]);
        resetEditState();
    };

    useEffect(() => {
        if (!visible) {
            resetSearchState();
            resetEditState();
            setCancelReject({ mode: false, note: '' });
            return;
        }

        // Dialog her açıldığında cancel reject state'ini sıfırla.
        setCancelReject({ mode: false, note: '' });

        if (isCreateMode) {
            resetCreateState();
            return;
        }

        if (!detailData) return;

        // Inspection mode: API artık iki tutarı da döndürüyor.
        //   detailData.totalAmount   -> KDV hariç (Toplam Tutar gösterimi)
        //   detailData.netTotalAmount -> KDV dahil (KDV'li Tutar gösterimi)
        // Eski response'larla geriye dönük uyum için totalAmount yoksa netTotalAmount'a düşeriz.
        const loadedTotal = detailData.totalAmount ?? detailData.netTotalAmount ?? 0;
        const loadedKdvli = detailData.netTotalAmount ?? 0;
        // Onay/İptal Onay modunda depoyu zorla kullanıcının ilk deposuna set et (gelen satışın deposu
        // başka bir bayinin deposu olabilir; detailData.warehouseId'yi yoksay).
        const forcedWarehouseId = (isApprovalMode || isCancelApprovalMode)
            ? (warehouseOptions[0]?.value ?? null)
            : detailData.warehouseId;
        setHeader({
            invoiceDate: detailData.invoiceDate ? new Date(detailData.invoiceDate) : null,
            invoiceSeriNo: detailData.invoiceSeriNo || '',
            maturityDate: detailData.maturityDate ? new Date(detailData.maturityDate) : null,
            warehouseId: forcedWarehouseId,
            paymentMethod: detailData.paymentMethod,
            customer: detailData.customer,
            invoiceAddress: detailData.invoiceAddress || '',
            currency: detailData.currency || 'TRY',
            netTotalAmount: loadedTotal,
            kdvliTutar: loadedKdvli,
            note: detailData.note || ''
        });
        setParts(
            (detailData.items || []).map((item, idx) => ({
                ...item,
                _id: idx + 1,
                discountType: item.discountType ?? 0,
                discountRate: item.discountRate ?? 0,
                // Backend kullanımı: unitPrice = indirim öncesi, netUnitPrice = indirim sonrası net.
                // Frontend convention: netUnitPrice = indirim öncesi orijinal fiyat (brüt hesabı vs için).
                // Bu sebeple backend'in unitPrice'ını frontend'in netUnitPrice alanına eşlemiyoruz.
                netUnitPrice: item.unitPrice ?? item.netUnitPrice ?? 0,
                sellingPrice: item.netUnitPrice ?? 0
            }))
        );

        if (detailData.customer) {
            const ct = detailData?.customerType ?? customerType;
            if (ct === 0) {
                setEditCustomerId(detailData.customer);
                setEditDealerId(null);
                setDealerFromId(null);
            } else {
                setEditDealerId(detailData.customer);
                setEditCustomerId(null);
                setCustomerFromId(null);
            }
        }
    }, [visible, isCreateMode, detailData, customerType, isApprovalMode, isCancelApprovalMode, warehouseOptions]);

    const calculateSellingPrice = (originalPrice, discountType, discountRate) => {
        if (!originalPrice || discountRate == null) return originalPrice;
        if (discountType === 1) {
            return originalPrice - (originalPrice * discountRate) / 100;
        } else if (discountType === 2) {
            return originalPrice - discountRate;
        }
        return originalPrice;
    };

    const calculateDiscountRate = (originalPrice, sellingPrice, discountType) => {
        if (!originalPrice || sellingPrice == null) return 0;
        if (discountType === 1) {
            return ((originalPrice - sellingPrice) / originalPrice) * 100;
        } else if (discountType === 2) {
            return originalPrice - sellingPrice;
        }
        return 0;
    };

    // Fetch and apply discount rates when customer changes
    const fetchAndApplyDiscountRates = async (customerId) => {
        if (!customerId) {
            setCurrentPartsDiscountRate(0);
            return;
        }
        
        try {
            const result = await getCustomerDiscountRates(Number(customerId)).unwrap();
            if (result) {
                const discountRate = result.partsDiscountRate || 0;
                setCurrentPartsDiscountRate(discountRate);
                
                // Apply discount to existing parts (using percentage discount type)
                const updatedParts = parts.map(part => {
                    const newSellingPrice = calculateSellingPrice(part.netUnitPrice, 1, discountRate);
                    return {
                        ...part,
                        discountType: 1, // Yüzde
                        discountRate: discountRate,
                        sellingPrice: newSellingPrice
                    };
                });
                setParts(updatedParts);
                updateTotalAmount(updatedParts);
                
                toast.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('warranty.customerDeclaration.repairClose.discountRatesUpdated') || 'İndirim oranları güncellendi',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Discount rates fetch error:', error);
            setCurrentPartsDiscountRate(0);
        }
    };
    
    // Handle customer change
    const handleCustomerChange = async (newCustomerId) => {
        setHeader((h) => ({ ...h, customer: newCustomerId }));
        
        // Only fetch discount rates for customers (customerType === 0), not dealers
        if (newCustomerId && customerType === 0) {
            await fetchAndApplyDiscountRates(newCustomerId);
        } else {
            setCurrentPartsDiscountRate(0);
        }
    };

    const updateTotalAmount = useCallback((updatedParts) => {
        const total = updatedParts.reduce((sum, part) => {
            const qty = part.quantity || 1;
            // Use ?? so a legitimate sellingPrice of 0 (e.g. 100% indirim) is respected
            const price = part.sellingPrice ?? part.netUnitPrice ?? 0;
            return sum + qty * price;
        }, 0);
        setHeader((prev) => ({
            ...prev,
            netTotalAmount: total,
            kdvliTutar: total * kdvFactor
        }));
    }, [kdvFactor]);

    const handleNetTotalEdit = useCallback((newValue) => {
        const v = newValue ?? 0;
        setHeader((h) => ({ ...h, netTotalAmount: v, kdvliTutar: v * kdvFactor }));
    }, [kdvFactor]);

    const handleKdvliTutarEdit = useCallback((newValue) => {
        const v = newValue ?? 0;
        const net = kdvFactor > 0 ? v / kdvFactor : 0;
        setHeader((h) => ({ ...h, netTotalAmount: net, kdvliTutar: v }));
    }, [kdvFactor]);

    const fetchPartsList = useCallback(
        async (warehouseId, searchText = '') => {
            if (!warehouseId) {
                setPartOptions([]);
                return;
            }

            setIsLoadingParts(true);
            try {
                const result = await searchPartList({
                    LanguageId: languageId,
                    WarehouseId: warehouseId,
                    SearchText: searchText || ''
                }).unwrap();

                const options = (result || []).map((p) => ({
                    label: `${p.code} - ${p.name}`,
                    value: p.code,
                    code: p.code,
                    name: p.name
                }));
                setPartOptions(options);
            } catch (error) {
                setPartOptions([]);
            } finally {
                setIsLoadingParts(false);
            }
        },
        [languageId, searchPartList]
    );

    useEffect(() => {
        if (visible && isCreateMode && header.warehouseId) {
            setPartFilterValue('');
            setPartSearchText('');
            setDebouncedPartSearch('');
            fetchPartsList(header.warehouseId, '');
        }
    }, [visible, isCreateMode, header.warehouseId, fetchPartsList]);

    useEffect(() => {
        if (visible && isCreateMode && header.warehouseId) {
            fetchPartsList(header.warehouseId, debouncedPartSearch);
        }
    }, [visible, isCreateMode, header.warehouseId, debouncedPartSearch, fetchPartsList]);

    const handlePartSelect = async (e) => {
        const selectedValue = e.value;
        setSelectedPartCode(selectedValue);
        
        if (!selectedValue) {
            return;
        }

        const selectedOption = partOptions.find(opt => opt.value === selectedValue);
        if (!selectedOption) {
            return;
        }

        try {
            const result = await searchPart({
                WarehouseId: header.warehouseId,
                LanguageId: languageId,
                PartCode: selectedOption.value
            }).unwrap();

            if (result) {
                const discountRate = customerType === 0 ? (currentPartsDiscountRate || 0) : 0;
                const sellingPrice = discountRate > 0 
                    ? calculateSellingPrice(result.unitPrice, 1, discountRate) 
                    : result.unitPrice;
                
                const newPart = {
                    _id: Date.now(),
                    partCode: result.partCode,
                    partName: result.partName,
                    quantity: 1,
                    netUnitPrice: result.unitPrice,
                    meins: result.meins,
                    stock: result.stock,
                    isKarsan: result.isKarsan,
                    discountType: discountRate > 0 ? 1 : 0, // 1 = Yüzde if discount exists
                    discountRate: discountRate,
                    sellingPrice: sellingPrice
                };
                const updatedParts = [...parts, newPart];
                setParts(updatedParts);
                updateTotalAmount(updatedParts);
                setSelectedPartCode(null);
                setPartFilterValue('');
                toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('retailSales.partAdded'), life: 3000 });
            }
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('retailSales.partNotFound');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const handleRemovePart = (partId) => {
        const updatedParts = parts.filter((p) => p._id !== partId);
        setParts(updatedParts);
        updateTotalAmount(updatedParts);
    };

    const onCellEditComplete = (e) => {
        const { rowData, newValue, field } = e;

        const updatedParts = parts.map((part) => {
            if (part._id !== rowData._id) {
                return part;
            }

            let updated = { ...part, [field]: newValue };

            switch (field) {
                case 'discountType':
                    updated.discountRate = 0;
                    updated.sellingPrice = part.netUnitPrice;
                    break;
                case 'discountRate':
                    updated.sellingPrice = calculateSellingPrice(part.netUnitPrice, part.discountType, newValue);
                    break;
                case 'sellingPrice':
                    updated.discountRate = calculateDiscountRate(part.netUnitPrice, newValue, part.discountType);
                    break;
                default:
                    break;
            }

            return updated;
        });

        setParts(updatedParts);
        updateTotalAmount(updatedParts);
    };

    const handleSave = async () => {
        if (!header.invoiceSeriNo || !header.invoiceSeriNo.trim()) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.enterInvoiceSeriNo'), life: 3000 });
            return;
        }
        if (!header.maturityDate) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.selectMaturityDate'), life: 3000 });
            return;
        }
        if (!header.warehouseId) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.selectWarehouse'), life: 3000 });
            return;
        }
        if (header.paymentMethod == null) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.selectPaymentMethod'), life: 3000 });
            return;
        }
        if (!header.customer) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.selectCustomer'), life: 3000 });
            return;
        }
        if (!header.invoiceAddress || !header.invoiceAddress.trim()) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.enterInvoiceAddress'), life: 3000 });
            return;
        }
        if (parts.length === 0) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.addAtLeastOnePart'), life: 3000 });
            return;
        }

        const payload = {
            invoiceDate: header.invoiceDate ? header.invoiceDate.toISOString() : new Date().toISOString(),
            invoiceSeriNo: header.invoiceSeriNo || '',
            maturityDate: header.maturityDate ? header.maturityDate.toISOString() : null,
            warehouseId: header.warehouseId,
            paymentMethod: header.paymentMethod ?? 0,
            customerType: customerType,
            customer: header.customer,
            invoiceAddress: header.invoiceAddress || '',
            // netTotalAmount: KDV dahil tutar; totalAmount: KDV hariç tutar
            netTotalAmount: header.kdvliTutar ?? 0,
            totalAmount: header.netTotalAmount ?? 0,
            currency: header.currency || 'TRY',
            note: header.note || '',
            items: parts.map((p) => ({
                partCode: p.partCode,
                partName: p.partName,
                quantity: p.quantity || 1,
                netUnitPrice: p.sellingPrice ?? p.netUnitPrice,
                discountType: p.discountType || 0,
                discountRate: p.discountRate || 0
            }))
        };

        try {
            await createRetail(payload).unwrap();
            toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('retailSales.saveSuccess'), life: 3000 });
            onSuccess?.();
            setTimeout(() => onHide(), 1000);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const quantityEditor = (options) => <InputNumber value={options.value} onValueChange={(e) => options.editorCallback(e.value)} min={1} style={{ width: '100%' }} />;

    const discountTypeEditor = (options) => <Dropdown value={options.value} options={discountTypes} onChange={(e) => options.editorCallback(e.value)} style={{ width: '100%' }} />;

    const discountRateEditor = (options) => {
        const discountType = options.rowData?.discountType ?? 0;
        const netUnitPrice = options.rowData?.netUnitPrice ?? 0;
        return (
            <InputNumber
                value={options.value ?? 0}
                onValueChange={(e) => options.editorCallback(e.value ?? 0)}
                min={0}
                max={discountType === 1 ? 100 : netUnitPrice}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                locale={browserLocale}
                style={{ width: '100%' }}
            />
        );
    };

    const priceEditor = (options) => (
        <InputNumber
            value={options.value ?? 0}
            onValueChange={(e) => options.editorCallback(e.value ?? 0)}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={2}
            locale={browserLocale}
            min={0}
            style={{ width: '100%' }}
        />
    );

    const isFormDisabled = !isCreateMode;

    const handleApprove = async () => {
        if (!retailId || !header.warehouseId) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('retailSales.selectWarehouse'), life: 3000 });
            return;
        }

        try {
            await approveOrReject({
                retailId: retailId,
                wareHouseId: header.warehouseId,
                approveDealerRetailMessage: 1
            }).unwrap();
            toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('retailSales.approveSuccess'), life: 3000 });
            onSuccess?.();
            setTimeout(() => onHide(), 1000);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const handleReject = async () => {
        if (!retailId) return;

        try {
            await approveOrReject({
                retailId: retailId,
                wareHouseId: header.warehouseId || 0,
                approveDealerRetailMessage: 2
            }).unwrap();
            toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('retailSales.rejectSuccess'), life: 3000 });
            onSuccess?.();
            setTimeout(() => onHide(), 1000);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const handleApproveCancel = async () => {
        if (!retailId) return;
        try {
            await approveOrRejectCancel({
                retailId: retailId,
                approveCancelDealerRetailMessage: 1,
                rejectNote: null
            }).unwrap();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('retailSales.cancelApproveSuccess'),
                life: 3000
            });
            onSuccess?.();
            setTimeout(() => onHide(), 800);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const handleRejectCancelSubmit = async () => {
        if (!retailId) return;
        if (!cancelRejectNote?.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: th('retailSales.rejectNoteRequired'),
                life: 3000
            });
            return;
        }
        try {
            await approveOrRejectCancel({
                retailId: retailId,
                approveCancelDealerRetailMessage: 2,
                rejectNote: cancelRejectNote.trim()
            }).unwrap();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('retailSales.cancelRejectSuccess'),
                life: 3000
            });
            onSuccess?.();
            setTimeout(() => onHide(), 800);
        } catch (error) {
            const errorMessage = error?.data?.errorMessage || th('common.error');
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: errorMessage, life: 3000 });
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('common.close')}
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={isProcessingCancel}
            />
            {isCreateMode && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isCreating} disabled={isCreating} />}
            {isApprovalMode && (
                <>
                    <Button label={th('retailSales.reject')} icon="pi pi-times" severity="danger" onClick={handleReject} loading={isApproving} disabled={isApproving} />
                    <Button label={th('retailSales.approve')} icon="pi pi-check" severity="success" onClick={handleApprove} loading={isApproving} disabled={isApproving} />
                </>
            )}
            {isCancelApprovalMode && !cancelRejectMode && (
                <>
                    <Button
                        label={th('retailSales.rejectCancel')}
                        icon="pi pi-times"
                        severity="danger"
                        onClick={() => setCancelReject((s) => ({ ...s, mode: true }))}
                        disabled={isProcessingCancel}
                    />
                    <Button
                        label={th('retailSales.approveCancel')}
                        icon="pi pi-check"
                        severity="success"
                        onClick={handleApproveCancel}
                        loading={isProcessingCancel}
                        disabled={isProcessingCancel}
                    />
                </>
            )}
            {isCancelApprovalMode && cancelRejectMode && (
                <>
                    <Button
                        label={th('common.back')}
                        icon="pi pi-arrow-left"
                        onClick={() => setCancelReject({ mode: false, note: '' })}
                        className="p-button-text"
                        disabled={isProcessingCancel}
                    />
                    <Button
                        label={th('retailSales.rejectCancelSubmit')}
                        icon="pi pi-send"
                        severity="danger"
                        onClick={handleRejectCancelSubmit}
                        loading={isProcessingCancel}
                        disabled={isProcessingCancel}
                    />
                </>
            )}
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={
                    isCreateMode
                        ? th('retailSales.createTitle')
                        : isApprovalMode
                            ? th('retailSales.approvalTitle')
                            : isCancelApprovalMode
                                ? th('retailSales.cancelApprovalTitle')
                                : th('retailSales.detailTitle')
                }
                style={{ width: '90vw', maxWidth: 1200 }}
                footer={footer}
                maximizable
            >
                <div className="grid mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.invoiceSeriNo')} <span className="text-red-500">*</span>
                        </label>
                        <InputText value={header.invoiceSeriNo} onChange={(e) => setHeader((h) => ({ ...h, invoiceSeriNo: e.target.value }))} className="w-full" disabled={isFormDisabled} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2 font-semibold">{th('retailSales.invoiceDate')}</label>
                        <Calendar value={header.invoiceDate} onChange={(e) => setHeader((h) => ({ ...h, invoiceDate: e.value }))} dateFormat="dd/mm/yy" showIcon className="w-full" disabled={isFormDisabled} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.maturityDate')} <span className="text-red-500">*</span>
                        </label>
                        <Calendar value={header.maturityDate} onChange={(e) => setHeader((h) => ({ ...h, maturityDate: e.value }))} dateFormat="dd/mm/yy" showIcon className="w-full" disabled={isFormDisabled} />
                    </div>
                    {!isCancelApprovalMode && (
                        <div className="col-12 md:col-3">
                            <label
                                className="block mb-2 font-semibold"
                                style={isApprovalMode ? { color: '#d97706' } : undefined}
                            >
                                {th('retailSales.warehouse')} <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={header.warehouseId}
                                options={warehouseOptions}
                                onChange={(e) => setHeader((h) => ({ ...h, warehouseId: e.value }))}
                                placeholder={th('retailSales.selectWarehouse')}
                                className="w-full"
                                style={isApprovalMode ? { borderColor: '#f59e0b', borderWidth: '2px', boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.15)' } : undefined}
                                disabled={isFormDisabled && !isApprovalMode}
                            />
                        </div>
                    )}
                    <div className="col-12 md:col-3">
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.paymentMethod')} <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            value={header.paymentMethod}
                            options={paymentMethodOptions}
                            onChange={(e) => setHeader((h) => ({ ...h, paymentMethod: e.value }))}
                            placeholder={th('retailSales.selectPaymentMethod')}
                            className="w-full"
                            disabled={isFormDisabled}
                        />
                    </div>
                    {!isApprovalMode && !isCancelApprovalMode && (
                        <div className="col-12 md:col-3">
                            <label className="block mb-2 font-semibold">
                                {effectiveCustomerType === 0 ? th('retailSales.customer') : th('preAccounting.dealer')} <span className="text-red-500">*</span>
                            </label>
                            {effectiveCustomerType === 0 ? (
                                <Dropdown
                                    value={header.customer}
                                    options={customerOptions}
                                    onChange={(e) => handleCustomerChange(e.value)}
                                    onFilter={(e) => {
                                        setCustomerFilterValue(e.filter);
                                        setCustomerSearchText(e.filter);
                                    }}
                                    filterValue={customerFilterValue}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={th('retailSales.selectCustomer')}
                                    className="w-full"
                                    disabled={isFormDisabled}
                                    filter
                                    showClear
                                    resetFilterOnHide={false}
                                    emptyMessage={th('common.noRecords')}
                                    emptyFilterMessage={th('common.noRecords')}
                                />
                            ) : (
                                <Dropdown
                                    value={header.customer}
                                    options={dealerOptions}
                                    onChange={(e) => handleCustomerChange(e.value)}
                                    onFilter={(e) => {
                                        setDealerFilterValue(e.filter);
                                        setDealerSearchText(e.filter);
                                    }}
                                    filterValue={dealerFilterValue}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={th('preAccounting.selectDealer')}
                                    className="w-full"
                                    disabled={isFormDisabled}
                                    filter
                                    showClear
                                    resetFilterOnHide={false}
                                    emptyMessage={th('common.noRecords')}
                                    emptyFilterMessage={th('common.noRecords')}
                                />
                            )}
                        </div>
                    )}
                    <div className="col-12 md:col-6">
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.invoiceAddress')} <span className="text-red-500">*</span>
                        </label>
                        <InputText value={header.invoiceAddress} onChange={(e) => setHeader((h) => ({ ...h, invoiceAddress: e.target.value }))} className="w-full" disabled={isFormDisabled} />
                    </div>
                    <div className="col-12">
                        <label className="block mb-2 font-semibold">{th('retailSales.note')}</label>
                        <InputTextarea
                            value={header.note}
                            onChange={(e) => setHeader((h) => ({ ...h, note: e.target.value }))}
                            placeholder={th('retailSales.notePlaceholder')}
                            className="w-full"
                            rows={2}
                            autoResize
                            disabled={isFormDisabled}
                        />
                    </div>
                </div>

                {isCreateMode && (
                    <div className="mb-3" style={{ maxWidth: 500 }}>
                        <label className="block mb-2 font-semibold">{th('retailSales.partCode')}</label>
                        <Dropdown
                            value={selectedPartCode}
                            options={partOptions}
                            onChange={handlePartSelect}
                            placeholder={th('retailSales.searchPartPlaceholder')}
                            className="w-full"
                            filter
                            filterValue={partFilterValue}
                            onFilter={(e) => {
                                setPartFilterValue(e.filter);
                                setPartSearchText(e.filter);
                            }}
                            showClear
                            disabled={!header.warehouseId}
                            loading={isLoadingParts}
                            emptyMessage={th('common.noRecords')}
                            emptyFilterMessage={th('common.noRecords')}
                            resetFilterOnHide={false}
                        />
                        {!header.warehouseId && <small className="text-orange-500">{th('retailSales.selectWarehouseFirst')}</small>}
                    </div>
                )}

                <DataTable value={parts} dataKey="_id" emptyMessage={th('common.noRecords')} editMode={isCreateMode ? 'cell' : null} className="editable-cells-table" showGridlines stripedRows>
                    <Column field="partCode" header={th('retailSales.partCode')} style={{ width: '12%' }} />
                    <Column field="partName" header={th('retailSales.partName')} style={{ width: '20%' }} />
                    <Column field="meins" header={th('retailSales.unit')} style={{ width: '8%', textAlign: 'center' }} />
                    <Column field="stock" header={th('retailSales.stock')} style={{ width: '8%', textAlign: 'center' }} />
                    <Column
                        field="quantity"
                        header={th('retailSales.quantity')}
                        editor={isCreateMode ? quantityEditor : null}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '10%', textAlign: 'center', backgroundColor: isCreateMode ? '#f0f8ff' : 'inherit' }}
                    />
                    <Column
                        field="netUnitPrice"
                        header={th('retailSales.unitPrice')}
                        body={(row) => (row.netUnitPrice || 0).toLocaleString(browserLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        style={{ width: '10%', textAlign: 'right' }}
                    />
                    <Column
                        field="discountType"
                        header={th('retailSales.discountType')}
                        body={(row) => discountTypes.find((d) => d.value === row.discountType)?.label || 'Yok'}
                        editor={isCreateMode ? discountTypeEditor : null}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '10%', backgroundColor: isCreateMode ? '#f0f8ff' : 'inherit' }}
                    />
                    <Column
                        field="discountRate"
                        header={th('retailSales.discountRate')}
                        body={(row) => {
                            const formatted = (row.discountRate || 0).toLocaleString(browserLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            return row.discountType === 1 ? `${formatted}%` : formatted;
                        }}
                        editor={isCreateMode ? discountRateEditor : null}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '10%', textAlign: 'right', backgroundColor: isCreateMode ? '#f0f8ff' : 'inherit' }}
                    />
                    <Column
                        field="sellingPrice"
                        header={th('retailSales.sellingPrice')}
                        body={(row) => (row.sellingPrice ?? 0).toLocaleString(browserLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        editor={isCreateMode ? priceEditor : null}
                        onCellEditComplete={onCellEditComplete}
                        style={{ width: '10%', textAlign: 'right', backgroundColor: isCreateMode ? '#f0f8ff' : 'inherit' }}
                    />
                    {isCreateMode && <Column body={(rowData) => <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm" onClick={() => handleRemovePart(rowData._id)} />} style={{ width: '5%' }} />}
                </DataTable>

                {isCancelApprovalMode && cancelInfo && (
                    <div className="mt-4 p-3" style={{ backgroundColor: '#fff7e6', borderRadius: '6px', borderLeft: '4px solid #f59e0b' }}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="mb-2">
                                    <span className="font-semibold">{th('retailSales.cancelRequestedBy')}: </span>
                                    <span>{cancelInfo.cancelRequestedName || '-'}</span>
                                </div>
                                <div>
                                    <span className="font-semibold">{th('retailSales.cancelRequestedDate')}: </span>
                                    <span>{cancelInfo.cancelRequestedDate ? new Date(cancelInfo.cancelRequestedDate).toLocaleString('tr-TR') : '-'}</span>
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="font-semibold mb-1">{th('retailSales.cancelRequestNote')}</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{cancelInfo.cancelRequestNote || '-'}</div>
                            </div>
                        </div>
                    </div>
                )}

                {isCancelApprovalMode && cancelRejectMode && (
                    <div className="mt-3 p-3" style={{ backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                        <label className="block mb-2 font-semibold">
                            {th('retailSales.rejectNote')} <span className="text-red-500">*</span>
                        </label>
                        <InputTextarea
                            value={cancelRejectNote}
                            onChange={(e) => setCancelReject((s) => ({ ...s, note: e.target.value }))}
                            rows={3}
                            maxLength={500}
                            placeholder={th('retailSales.rejectNotePlaceholder')}
                            className="w-full"
                            disabled={isProcessingCancel}
                        />
                        <small className="text-500">{cancelRejectNote.length}/500</small>
                    </div>
                )}

                <div className="mt-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', borderTop: '2px solid #dee2e6' }}>
                    <div className="grid">
                        <div className="col-12 md:col-6 md:col-offset-6">
                            <div className="flex justify-content-between align-items-center mb-2">
                                <label className="text-base text-700">{th('retailSales.grossTotalAmount')}</label>
                                <span className="text-base text-700">
                                    {grossTotalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {header.currency}
                                </span>
                            </div>
                            <div className="flex justify-content-between align-items-center mb-2">
                                <label className="font-bold text-lg">{th('retailSales.totalAmount')}</label>
                                {isCreateMode ? (
                                    <div className="flex align-items-center gap-2">
                                        <InputNumber
                                            value={header.netTotalAmount ?? 0}
                                            onValueChange={(e) => handleNetTotalEdit(e.value)}
                                            mode="decimal"
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            locale={browserLocale}
                                            min={0}
                                            inputClassName="font-bold text-xl text-right"
                                            inputStyle={{ width: '180px' }}
                                        />
                                        <span className="font-bold text-xl">{header.currency}</span>
                                    </div>
                                ) : (
                                    <span className="font-bold text-xl">
                                        {(header.netTotalAmount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {header.currency}
                                    </span>
                                )}
                            </div>
                            {showKdvBlock && (
                                <>
                                    <div className="flex justify-content-between align-items-center">
                                        <label className="font-bold text-lg text-primary">
                                            {th('preAccounting.kdvliTutar')}
                                            {displayKdvRate != null && ` (${displayKdvRate}% KDV)`}
                                        </label>
                                        {isCreateMode ? (
                                            <div className="flex align-items-center gap-2">
                                                <InputNumber
                                                    value={header.kdvliTutar ?? 0}
                                                    onValueChange={(e) => handleKdvliTutarEdit(e.value)}
                                                    mode="decimal"
                                                    minFractionDigits={2}
                                                    maxFractionDigits={2}
                                                    locale={browserLocale}
                                                    min={0}
                                                    inputClassName="font-bold text-xl text-right text-primary"
                                                    inputStyle={{ width: '180px' }}
                                                />
                                                <span className="font-bold text-xl text-primary">{header.currency}</span>
                                            </div>
                                        ) : (
                                            <span className="font-bold text-xl text-primary">
                                                {(header.kdvliTutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {header.currency}
                                            </span>
                                        )}
                                    </div>
                                    <small className="text-500 block mt-1" style={{ fontSize: '0.75rem' }}>
                                        {th('retailSales.kdvNote')}
                                    </small>
                                </>
                            )}
                            <div className="mt-3 pt-3" style={{ borderTop: '1px dashed #ced4da' }}>
                                <div className="flex justify-content-between align-items-center">
                                    <label className="font-semibold text-base text-orange-600">
                                        {th('retailSales.totalDiscount')}
                                    </label>
                                    <span className="font-semibold text-base text-orange-600">
                                        {totalDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {header.currency}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default RetailSalesDetailDialog;
